import React, { useState, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet";
import { parameterValueService } from "/app/src/services";
import { useTranslation } from "react-i18next";
import { ParameterValue as ParameterValueType } from "/app/src/models";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
import { useSearchParams } from "react-router-dom";
import useQueryParams from "/app/src/hooks/useQueryParams";
import {
  Button,
  Checkbox,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@heroui/react";

import useSetting from "/app/src/components/settings/setting";
import {
  buildExplorerColumns,
  isColumnSelected,
  toggleExplorerColumn,
} from "/app/src/components/explorer/helpers/columns";
import HeroSearch from "/app/src/components/generic/components/heroSearch";
import Breadcrumbs from "/app/src/components/generic/components/breadcrumbs";
import { useAuthState } from "/app/src/contexts/authentication";

/**
 * Component for displaying all parameter values in a table
 */
export default function ParameterValues() {
  const { t } = useTranslation();

  const [sort, setSort] = useSortUpgrade([]);
  const [searchParams] = useSearchParams();

  const [searchString, setSearchString] = useState(
    searchParams.get("search") || "",
  );
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );

  const { user } = useAuthState();
  const columnHelper = createColumnHelper<ParameterValueType>();

  const availableColumns = [
    { id: "stationName", label: t("translation:station") },
    { id: "value", label: t("translation:value") },
  ];

  const { settingValue, createUpdateSetting } = useSetting({
    name: "explorerColumns",
    userId: user?.id,
  });

  const isColumnChecked = useCallback(
    (id: string) => isColumnSelected(settingValue, "parameter_value", id),
    [settingValue],
  );

  const toggleColumnSelection = useCallback(
    async (id: string) =>
      toggleExplorerColumn(
        settingValue,
        createUpdateSetting,
        "parameter_value",
        id,
        user?.id,
      ),
    [settingValue, createUpdateSetting, user?.id],
  );

  const columns = useMemo(() => {
    /**
     * Creates a column definition based on the given id.
     * @param {string} id - The identifier of the column to create.
     * @returns {Object|null} The column definition object or null if the id is not recognized.
     */
    const makeColumn = (id: string) => {
      switch (id) {
        case "name":
          return columnHelper.accessor("name", {
            id: "name",
            header: () => t("translation:name"),
            cell: (info) => info.getValue(),
          });
        case "stationName":
          return columnHelper.accessor("stationName", {
            id: "stationName",
            cell: (info) => info.getValue(),
            header: () => t("translation:station"),
          });
        case "value":
          return columnHelper.accessor("value", {
            id: "value",
            cell: (info) => {
              const raw = info.getValue();
              const str =
                typeof raw === "string" ? raw : raw ? String(raw) : "";
              const preview =
                str.length > 200 ? `${str.substring(0, 200)}...` : str;

              return (
                <div
                  className="max-w-[40rem] break-words text-sm"
                  title={str}
                  style={{ wordBreak: "break-word" }}
                >
                  {preview}
                </div>
              );
            },
            header: () => t("translation:value"),
          });
        default:
          return null;
      }
    };
    return buildExplorerColumns(
      settingValue,
      "parameter_value",
      makeColumn,
      "name",
    );
  }, [settingValue, t, columnHelper]);

  const createToggleHandler = useCallback(
    (id: string) => () => {
      toggleColumnSelection(id);
    },
    [toggleColumnSelection],
  );

  const {
    data: parameterValues,
    count: parameterValuesCount,
    isFetching,
  } = usePaginatedData<ParameterValueType>({
    queryKey: ["parameter_values"],
    searchString,
    sort,
    page,
    pageSize,
    service: parameterValueService,
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count: parameterValuesCount,
    isFetching,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  return (
    <div className="explorer">
      <Helmet>
        <title>{t("translation:parameter_values")} - ItemPath</title>
      </Helmet>
      <Breadcrumbs
        items={[
          { label: "Explorer", to: "/explorer" },
          { label: t("translation:parameter_values") },
        ]}
      />

      <div className="mt-12 mb-4 flex justify-between">
        <div className="flex justify-between gap-2">
          <HeroSearch
            className="w-[300px]"
            setSearchString={setSearchString}
            allowClear
            box={false}
          />
        </div>
        <Popover placement="bottom" radius="md" aria-label="Customize columns">
          <PopoverTrigger>
            <Button size="md" radius="md" color="primary">
              {t("translation:customize_columns")}
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            <div className="w-[300px] p-4">
              <h3 className="text-[16px]">
                {t("translation:available_columns")}
              </h3>
              <div className="mt-2 flex flex-col gap-2">
                {availableColumns.map((column) => (
                  <Checkbox
                    size="sm"
                    key={column.id}
                    defaultSelected={isColumnChecked(column.id)}
                    onChange={createToggleHandler(column.id)}
                  >
                    {column.label}
                  </Checkbox>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <Table
        loading={isFetching}
        rows={parameterValues}
        tableColumns={columns}
        length={parameterValuesCount}
        sort={sort}
        setSort={setSort}
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPage: updatePage,
          setPageSize: updatePageSize,
        }}
        emptyText={t("translation:no_parameter_values_found")}
      />
    </div>
  );
}
